import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ArticleNameCell, BaseMoneyCell } from '@/components/ArticlesTableCells';
import {
  ArticleOrganizationPriceCardItem,
  ArticleWorkshopPriceCardItem,
} from '@/components/ArticlesTableCells/ArticleMoneyCell/ArticleMoneyCell';
import {
  ArticleServiceTypeCardItem,
  ArticleServiceTypeCell,
} from '@/components/ArticlesTableCells/ArticleServiceTypeCell';
import { ArticleStepper } from '@/components/ArticleStepper/ArticleStepper';
import { Card, CardContent, CardItems } from '@/components/Card/Card';
import { StatusDueDate } from '@/components/DueDate';
import { useNavigate } from '@/components/Link/Link';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import IconChevron from '@/icons/Chevron.svg';
import { hasActions, useArticleName } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import { shouldShowPendingOrganizationQuote, shouldShowPendingWorkshopQuote } from '@/utils/price';

import './OnGoingArticlesTable.css';

const { block, element } = createBEMClasses('on-going-articles-table');

export const OnGoingArticlesTable = ({
  articles,
  mode,
}: {
  articles: ArticleWithRelations[];
  mode: 'task' | 'step';
}) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  return (
    <Table
      ariaLabel={t({ id: 'request.articles.table.label', message: 'Items' })}
      className={block()}
      columnWidths={[
        'minmax(200px, 1fr)',
        'minmax(120px, 1fr)',
        'minmax(120px, 1fr)',
        workshopPrice.shown && 'minmax(120px, max-content)',
        organizationPrice.shown && 'minmax(auto, max-content)',
      ]}
    >
      <OnGoingArticlesTableHeader mode={mode} />
      <Body>
        {articles.map((article) => (
          <OnGoingArticleRow key={article.id} article={article} mode={mode} />
        ))}
      </Body>
    </Table>
  );
};

const OnGoingArticlesTableHeader = ({ mode }: { mode: 'task' | 'step' }) => {
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  return (
    <Header>
      <Row>
        <Column>
          <Trans id="request.articles.table.label">Items</Trans>
        </Column>
        <Column>
          {mode === 'task' && (
            <Trans id="request.articles.table.column.task.label">Task to do</Trans>
          )}
          {mode === 'step' && <Trans id="request.articles.table.column.step.label">Step</Trans>}
        </Column>
        <Column>
          <Trans id="request.articles.table.column.service-type.label">Service Type</Trans>
        </Column>
        {workshopPrice.shown && (
          <Column justifyContent="flex-end" textAlign="right">
            {workshopPrice.label}
          </Column>
        )}
        {organizationPrice.shown && (
          <Column justifyContent="flex-end" textAlign="right">
            {organizationPrice.label}
          </Column>
        )}
      </Row>
    </Header>
  );
};

const OnGoingArticleRow = ({
  article,
  mode,
}: {
  article: ArticleWithRelations;
  mode: 'task' | 'step';
}) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  const clientRefusalBadgeText =
    article.cancellationDetail?.type === 'requestor_refusal'
      ? t({
          id: 'request.articles.cancelled-by-client',
          message: 'Client refusal',
        })
      : undefined;

  return (
    <Row to={`/requests/${article.requestId}/articles/${article.id}`}>
      <Cell isLink>
        <ArticleNameCell article={article} />
      </Cell>
      <Cell stretch>
        <Stack>
          <ArticleStepper size="medium" article={article} mode={mode} withoutStepper />
          {article.statusDueAt ? (
            <StatusDueDate
              date={new Date(article.statusDueAt)}
              displayDayMonthOnly
              variant="small"
            />
          ) : (
            '-'
          )}
        </Stack>
      </Cell>
      <Cell>
        <ArticleServiceTypeCell article={article} />
      </Cell>
      {workshopPrice.shown && (
        <Cell justifyContent="flex-end">
          <BaseMoneyCell
            price={article.snapshot.workshopPrice?.amountPerCurrency?.[0]}
            showAmountBeforeTaxes={workshopPrice.showAmountBeforeTaxes}
            emptyLabel={shouldShowPendingWorkshopQuote(article) ? undefined : '-'}
          />
        </Cell>
      )}
      {organizationPrice.shown && (
        <Cell justifyContent="flex-end">
          <BaseMoneyCell
            price={article.snapshot.organizationPrice?.amountPerCurrency?.[0]}
            clientRefusalBadgeText={clientRefusalBadgeText}
            showAmountBeforeTaxes={organizationPrice.showAmountBeforeTaxes}
            emptyLabel={shouldShowPendingOrganizationQuote(article) ? undefined : '-'}
          />
        </Cell>
      )}
    </Row>
  );
};

export const OnGoingArticlesCardList = ({
  articles,
  mode,
  label,
}: {
  articles: ArticleWithRelations[];
  mode: 'task' | 'step';
  label: string;
}) => {
  const navigate = useNavigate();
  const { request } = useRequestContext();

  return (
    <GridList
      className={block()}
      onAction={(key) => {
        navigate(`/requests/${request.id}/articles/${key}`);
      }}
      aria-label={label}
    >
      {articles.map((article) => (
        <OnGoingArticleCard key={article.id} article={article} mode={mode} />
      ))}
    </GridList>
  );
};

const OnGoingArticleCard = ({
  article,
  mode,
}: {
  article: ArticleWithRelations;
  mode: 'task' | 'step';
}) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  const clientRefusalBadgeText =
    article.cancellationDetail?.type === 'requestor_refusal'
      ? t({
          id: 'request.articles.cancelled-by-client',
          message: 'Client refusal',
        })
      : undefined;

  const articleName = useArticleName({ article });

  return (
    <GridListItem id={article.id} textValue={articleName} className={element('item')}>
      <Card>
        <CardContent>
          <Stack
            row
            gap="0.5rem"
            flexWrap="nowrap"
            justifyContent="space-between"
            alignItems="center"
          >
            <ArticleNameCell id={article.id} article={article} />
            <IconChevron right style={{ fontSize: '1.5rem' }} />
          </Stack>
          <ArticleStepper size="medium" article={article} mode={mode}>
            <>
              {article.statusDueAt ? (
                <StatusDueDate
                  date={new Date(article.statusDueAt)}
                  displayDayMonthOnly
                  variant="small"
                />
              ) : (
                '-'
              )}
            </>
          </ArticleStepper>
          <CardItems>
            {hasActions(article) && <ArticleServiceTypeCardItem article={article} />}
            {workshopPrice.shown && <ArticleWorkshopPriceCardItem article={article} />}
            {organizationPrice.shown && (
              <ArticleOrganizationPriceCardItem
                article={article}
                clientRefusalBadgeText={clientRefusalBadgeText}
              />
            )}
          </CardItems>
        </CardContent>
      </Card>
    </GridListItem>
  );
};
